import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CaretDown, Users, Buildings, Wrench, User, Check, Warning } from '@phosphor-icons/react';
import { RenoRecipientSearch, AddedRecipient } from './RenoRecipientSearch';
import { RenoAddEmailInline } from './RenoAddEmailInline';

export interface RecipientPerson {
  name: string;
  email: string;
}

export interface RecipientOption {
  key: string;
  label: string;
  sublabel?: string;
  icon: 'building' | 'unit' | 'supplier' | 'contact';
  recipients: RecipientPerson[];
  /** Reden waarom deze ontvanger niet gemaild kan worden, bv. een ontbrekend adres. */
  warning?: string;
  /**
   * Onderliggende fiche. Aanwezig => een ontbrekend e-mailadres kan hier meteen
   * aangevuld en bewaard worden.
   */
  entity?: { type: 'contact' | 'company'; id: number };
}

interface Props {
  options: RecipientOption[];
  selectedKeys: string[];
  onChange: (keys: string[]) => void;
  /**
   * Wanneer meegegeven, kan er bovenaan vrij gezocht worden naar eender welke
   * contactpersoon of firma — ook eentje die niet aan het ticket of project hangt.
   */
  onAddRecipient?: (recipient: AddedRecipient) => void;
  /** Aangeroepen nadat een ontbrekend adres bij een bestaande ontvanger bewaard is. */
  onRecipientEmailAdded?: (option: RecipientOption, email: string) => void;
  /**
   * Optioneel van buitenaf open/dicht zetten, zodat een waarschuwing elders
   * ("Geen e-mailadres: …") de gebruiker rechtstreeks naar dit paneel kan brengen —
   * daar staat immers het veld om het adres aan te vullen.
   * Zonder deze props beheert het component zijn eigen toestand.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ICONS = {
  building: <Buildings size={14} className="text-neutral-secondary-mid" />,
  unit: <Users size={14} className="text-neutral-secondary-mid" />,
  supplier: <Wrench size={14} className="text-neutral-secondary-mid" />,
  contact: <User size={14} className="text-neutral-secondary-mid" />,
};

const PANEL_WIDTH = 288;

/**
 * Multi-select voor mail-ontvangers van een renovatieticket:
 * hele gebouw / huurder van de unit / gekoppelde leveranciers / contactpersoon.
 * De dropdown rendert via een portal (fixed) zodat hij niet geklipt wordt door
 * een parent met overflow-hidden en niet achter andere elementen valt.
 */
export function RenoRecipientSelect({
  options,
  selectedKeys,
  onChange,
  onAddRecipient,
  onRecipientEmailAdded,
  open: openProp,
  onOpenChange,
}: Props) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      const waarde = typeof next === 'function' ? next(open) : next;
      setOpenState(waarde);
      onOpenChange?.(waarde);
    },
    [open, onOpenChange],
  );
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const computePos = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const left = Math.max(8, Math.min(r.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - 8));
    setPos({ top: r.bottom + 4, left });
  }, []);

  useEffect(() => {
    if (!open) return;
    computePos();
    const onScroll = () => computePos();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, computePos, setOpen]);

  const toggle = (key: string) => {
    onChange(selectedKeys.includes(key) ? selectedKeys.filter((k) => k !== key) : [...selectedKeys, key]);
  };

  const selectedCount = selectedKeys.length;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex h-7 items-center gap-1.5 rounded-md border border-input bg-background px-2 text-xs font-medium text-neutral-primary transition-colors hover:bg-muted"
        title="Kies ontvangers"
      >
        <Users size={13} />
        {selectedCount > 0 ? `Ontvangers (${selectedCount})` : 'Ontvangers kiezen'}
        <CaretDown size={11} className="text-neutral-secondary-mid" />
      </button>

      {open && pos &&
        createPortal(
          <div
            ref={panelRef}
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: PANEL_WIDTH }}
            className="z-[9999] rounded-lg border border-muted-foreground/20 bg-muted shadow-xl"
          >
            {onAddRecipient && (
              <RenoRecipientSearch
                onAdd={onAddRecipient}
                existingEmails={options.flatMap((o) => o.recipients.map((r) => r.email))}
              />
            )}
            <div className="max-h-[60vh] overflow-y-auto overscroll-contain py-1">
              {options.length === 0 ? (
                <p className="px-3 py-2 text-xs text-neutral-secondary-mid">Geen ontvangers beschikbaar</p>
              ) : (
                options.map((opt) => {
                  const isSelected = selectedKeys.includes(opt.key);
                  const disabled = opt.recipients.length === 0;

                  // Ontbrekend adres én een gekende fiche: hier meteen aanvullen.
                  if (disabled && opt.entity && onRecipientEmailAdded) {
                    return (
                      <div key={opt.key} className="flex items-start gap-2 bg-amber-50/60 px-3 py-2">
                        <Warning size={14} weight="fill" className="mt-0.5 shrink-0 text-amber-700" />
                        <div className="min-w-0 flex-1 space-y-1">
                          <span className="block truncate text-xs text-amber-900">{opt.label}</span>
                          <span className="block truncate text-[10px] text-amber-800">
                            {opt.warning ?? 'Geen e-mailadres bekend'}
                          </span>
                          <RenoAddEmailInline
                            compact
                            type={opt.entity.type}
                            id={opt.entity.id}
                            name={opt.label}
                            onSaved={(email) => onRecipientEmailAdded(opt, email)}
                          />
                        </div>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={opt.key}
                      type="button"
                      disabled={disabled}
                      onClick={() => toggle(opt.key)}
                      title={disabled ? opt.warning ?? 'Geen e-mailadres bekend' : undefined}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors ${
                        disabled ? 'cursor-not-allowed bg-amber-50/60' : 'hover:bg-neutral-primary/5'
                      }`}
                    >
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          isSelected ? 'border-moss-primary bg-moss-primary text-white' : 'border-neutral-primary/30'
                        }`}
                      >
                        {isSelected && <Check size={11} weight="bold" />}
                      </span>
                      {disabled ? <Warning size={14} weight="fill" className="text-amber-700" /> : ICONS[opt.icon]}
                      <span className="min-w-0 flex-1">
                        <span className={`block truncate ${disabled ? 'text-amber-900' : 'text-neutral-primary'}`}>
                          {opt.label}
                        </span>
                        {/* Bij een ontbrekend adres primeert de waarschuwing op het gewone sublabel. */}
                        {disabled ? (
                          <span className="block truncate text-[10px] text-amber-800">
                            {opt.warning ?? 'Geen e-mailadres bekend'}
                          </span>
                        ) : (
                          opt.sublabel && (
                            <span className="block truncate text-[10px] text-neutral-secondary-mid">{opt.sublabel}</span>
                          )
                        )}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
